import {
  BasilPlan,
  EditContextAnswerPatch,
  EditContextBody,
  EditContextNodePatch,
  EditContextStepPatch,
  RowCounts,
} from './writeTypes';

/**
 * W4 — batching (spec section 5.4).
 *
 * - hard cap of 200 nodes per request (`edit_context.js:235`; over it the server answers 422);
 * - the serialized body stays under ~80 KB, because `app.use(bodyParser.json())` carries no options
 *   (`editor-api/src/app.js:75`) and body-parser's default is 100 KB, whatever the route comment at
 *   `routes/v1/edit-context.js:10` claims (AD1:51-52 sets the practical ceiling and says measure);
 * - one chunk carries exactly ONE plan, and cuts inside it are on NODE boundaries only. The
 *   verdict is per plan — `simulateApply(before, plan)` applies one request (`verify.ts:135-139`) —
 *   so a request that merged several plans could never be verified against a plan. The caps below
 *   are therefore enforced per request, and a plan too big for one request is refused rather than
 *   split into bodies whose counts no plan predicts;
 * - a node id appears at most once in a body: duplicate ids are last-write-wins on the server
 *   (`edit_context.js:501-502`), which would make the counts unreconcilable, so the plans that touch
 *   one node are merged into one node patch and a repeated ADDRESS is refused outright (E1).
 */

/** `edit_context.js:235`. */
export const MAX_NODES_PER_REQUEST = 200;

/** AD1:51-52 — measured on the serialized body, well under body-parser's 100 KB default. */
export const MAX_BODY_BYTES = 80 * 1024;

export interface PlannedChunk {
  chunkIndex: number;
  planIds: string[];
  body: EditContextBody;
  byteLength: number;
  nodeCount: number;
  expectedCounts: RowCounts;
}

export interface ChunkRefusal {
  ok: false;
  reason: 'duplicate-address' | 'too-many-nodes' | 'body-too-large' | 'not-edit-context' | 'nothing-to-apply';
  message: string;
  addresses?: string[];
}

export interface ChunkSuccess {
  ok: true;
  chunks: PlannedChunk[];
}

export type ChunkOutcome = ChunkSuccess | ChunkRefusal;

/** UTF-8 length without `TextEncoder`, which jsdom does not always expose. */
export function utf8ByteLength(value: string): number {
  let bytes = 0;

  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      // a surrogate pair is four UTF-8 bytes and consumes two UTF-16 units
      bytes += 4;
      index += 1;
    } else {
      bytes += 3;
    }
  }

  return bytes;
}

export function serializedByteLength(body: unknown): number {
  return utf8ByteLength(JSON.stringify(body));
}

/**
 * The addresses one plan writes to, as stable strings. Two plans sharing an address would be two
 * writes to one row in a single body — refused (E1).
 */
export function planAddresses(plan: BasilPlan): string[] {
  const addresses: string[] = [];
  const nodes = plan.editContextPatch ? plan.editContextPatch.nodes : [];

  nodes.forEach((node) => {
    const nodeKey = `node:${String(node.id)}`;
    const steps = node.steps || [];

    if (steps.length === 0) {
      addresses.push(nodeKey);
    }

    steps.forEach((step) => {
      const stepKey = `${nodeKey}/step:${String(step.id)}`;
      const answers = step.answers || [];

      if (answers.length === 0) {
        addresses.push(stepKey);
      }

      answers.forEach((answer) => {
        addresses.push(`${stepKey}/answer:${String(answer.id)}`);
      });
    });
  });

  return addresses;
}

/** Every address written more than once across the given plans. */
export function findDuplicateAddresses(plans: readonly BasilPlan[]): string[] {
  const seen = new Set<string>();
  const duplicates: string[] = [];

  plans.forEach((plan) => {
    planAddresses(plan).forEach((address) => {
      if (seen.has(address)) {
        if (duplicates.indexOf(address) === -1) {
          duplicates.push(address);
        }

        return;
      }

      seen.add(address);
    });
  });

  return duplicates;
}

function addCounts(target: RowCounts, source?: RowCounts | null): RowCounts {
  if (!source) {
    return target;
  }

  return {
    updatedNodes: target.updatedNodes + source.updatedNodes,
    updatedSteps: target.updatedSteps + source.updatedSteps,
    updatedAnswers: target.updatedAnswers + source.updatedAnswers,
  };
}

function mergeAnswers(
  target: EditContextAnswerPatch[],
  source: readonly EditContextAnswerPatch[],
): EditContextAnswerPatch[] {
  const merged = target.slice();

  source.forEach((answer) => {
    const existing = merged.find((candidate) => String(candidate.id) === String(answer.id));

    if (existing) {
      Object.assign(existing, answer);

      return;
    }

    merged.push({ ...answer });
  });

  return merged;
}

function mergeSteps(
  target: EditContextStepPatch[],
  source: readonly EditContextStepPatch[],
): EditContextStepPatch[] {
  const merged = target.slice();

  source.forEach((step) => {
    const existing = merged.find((candidate) => String(candidate.id) === String(step.id));

    if (!existing) {
      merged.push({ ...step, answers: step.answers ? step.answers.map((answer) => ({ ...answer })) : undefined });

      return;
    }

    const answers = step.answers ? mergeAnswers(existing.answers || [], step.answers) : existing.answers;

    Object.assign(existing, step);

    if (answers) {
      existing.answers = answers;
    }
  });

  return merged;
}

function bodyForPlan(plan: BasilPlan): EditContextBody {
  const nodes: EditContextNodePatch[] = [];
  const source = plan.editContextPatch ? plan.editContextPatch.nodes : [];

  source.forEach((node) => {
    const key = String(node.id);
    const existing = nodes.find((candidate) => String(candidate.id) === key);

    if (!existing) {
      nodes.push({ ...node, steps: mergeSteps([], node.steps || []) });

      return;
    }

    existing.steps = mergeSteps(existing.steps || [], node.steps || []);
  });

  return { nodes };
}

/**
 * Cut the edit-context plans into sendable chunks. Refuses rather than sending anything that could
 * not be reconciled: a repeated address (E1), a single node bigger than the body ceiling (E12), or
 * a plan that is not an edit-context plan at all.
 */
export function chunkPlans(plans: readonly BasilPlan[]): ChunkOutcome {
  if (plans.length === 0) {
    return { ok: false, reason: 'nothing-to-apply', message: "There's nothing to apply." };
  }

  const foreign = plans.filter((plan) => plan.writePath !== 'edit-context');

  if (foreign.length > 0) {
    return {
      ok: false,
      reason: 'not-edit-context',
      message: "Basil can't make this kind of change.",
    };
  }

  const duplicates = findDuplicateAddresses(plans);

  if (duplicates.length > 0) {
    return {
      ok: false,
      reason: 'duplicate-address',
      message: 'Two of your changes touch the same place, so Basil paused them all.',
      addresses: duplicates,
    };
  }

  const chunks: PlannedChunk[] = [];

  for (let index = 0; index < plans.length; index += 1) {
    const plan = plans[index];
    const body = bodyForPlan(plan);
    const byteLength = serializedByteLength(body);

    if (body.nodes.length > MAX_NODES_PER_REQUEST) {
      return {
        ok: false,
        reason: 'too-many-nodes',
        message: 'This change touches too many scenes to send at once.',
      };
    }

    if (byteLength > MAX_BODY_BYTES) {
      return {
        ok: false,
        reason: 'body-too-large',
        message: 'This change is too big to send at once.',
      };
    }

    chunks.push({
      chunkIndex: chunks.length,
      planIds: [plan.planId],
      body,
      byteLength,
      nodeCount: body.nodes.length,
      expectedCounts: addCounts({ updatedNodes: 0, updatedSteps: 0, updatedAnswers: 0 }, plan.expectedCounts),
    });
  }

  return { ok: true, chunks };
}
